import { mergeClasses } from "@fluentui/react-components";
import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { MapContainer, Pane, ZoomControl } from "react-leaflet";
import { useMapSas } from "../../../hooks/useMapSas";
import { IFlightMarker } from "../../../models/airports/AirportConfig";
import { useAppSelector } from "../../../store/StoreHooks";
import { getAirportCode } from "../../../store/airport/AirportSelector";
import { getAirportConfig } from "../../../utils/common/CommonHelperFunctions";
import {
  mapApiUrl,
  MapErrorId,
  WORLD_BOUNDS,
} from "../../../utils/maps/MapConstants";
import { getAzureMapTileUrl } from "../../../utils/maps/MapHelperFunctions";
import DisplayMapErrors from "../DisplayMapErrors";
import TileLayerWithHeader from "../TileLayerWithHeader";
import AirportMarkers from "../map-markers/AirportMarkers";
import SelectedFlightMarker from "../map-markers/flight-markers/SelectedFlightMarker";

export interface ISingleFlightMapProps {
  flightmarkerDetails: IFlightMarker;
  isLive: Boolean;
  className?: string;
}

const SingleFlightMap: React.FC<ISingleFlightMapProps> = ({
  flightmarkerDetails,
  isLive = false,
  className,
}) => {
  const airport = useAppSelector((state) => getAirportCode(state));
  const airportMapDetail = getAirportConfig(airport);

  const {
    sas: mapSas,
    error: mapSasError,
    forceRefresh: refreshMapSas,
    needsReset: mapSasNeedsReset,
    acknowledgeReset: acknowledgeMapSasReset,
  } = useMapSas(airport);
  const azureBaseMapUrl = getAzureMapTileUrl(
    airport,
    mapApiUrl,
    "BASE",
    mapSas
  );

  let isInvalidFlight;

  if (!isLive) {
    isInvalidFlight = true;
  } else {
    isInvalidFlight =
      isLive &&
      (isNaN(Number(flightmarkerDetails.latitude)) ||
        isNaN(Number(flightmarkerDetails.longitude)))
        ? true
        : false;
  }

  const [mapApiFailures, setMapApiFailures] = useState<MapErrorId[]>(
    isInvalidFlight ? ["NO_FLIGHTS"] : []
  );
  const [tileLayerKey, setTileLayerKey] = useState(0);

  const mapRef = useRef<L.Map | null>(null);

  const selectedFlightMarkerDetails = flightmarkerDetails;

  const mapCenter = isInvalidFlight
    ? airportMapDetail.location
    : [flightmarkerDetails.latitude, flightmarkerDetails.longitude];

  useEffect(() => {
    !isLive &&
      setMapApiFailures((prev) =>
        prev.includes("NO_FLIGHTS") ? prev : [...prev, "NO_FLIGHTS"]
      );
  }, [isLive]);

  const handleMapApiFailure = useCallback(
    (errorID: MapErrorId, isReset = false) => {
      if (isReset) {
        setMapApiFailures((prev) => prev.filter((id) => id !== errorID));
        return;
      }

      setMapApiFailures((prevFailures) =>
        prevFailures.includes(errorID)
          ? prevFailures
          : [...prevFailures, errorID]
      );
    },
    [setMapApiFailures]
  );

  useEffect(() => {
    handleMapApiFailure("SAS", !mapSasError);
  }, [mapSasError, handleMapApiFailure]);

  useEffect(() => {
    if (mapSasNeedsReset) {
      setTileLayerKey((prev) => prev + 1);
      acknowledgeMapSasReset();
    }
  }, [mapSasNeedsReset, acknowledgeMapSasReset]);

  const handleMarkerClick = useCallback(
    (_flightID: string, _docId: string) => {},
    []
  );

  return (
    <section
      className={mergeClasses("w-100 h-100", className)}
      data-testid="single-flight-map-container"
    >
      <MapContainer
        center={mapCenter}
        zoom={7}
        style={{ height: "100%", width: "100%", borderRadius: "8px" }}
        zoomControl={false}
        minZoom={4}
        ref={mapRef}
        preferCanvas
        maxBounds={WORLD_BOUNDS}
        attributionControl={true}
        maxBoundsViscosity={0.8}
      >
        <ZoomControl position="bottomright" />
        {mapSas && (
          <TileLayerWithHeader
            key={tileLayerKey}
            id="BASE"
            url={azureBaseMapUrl}
            attribution="© Microsoft Azure Maps"
            layerOpacity={0.9}
            zIndex={100}
            keepBuffer={2}
            updateInterval={500}
            setMapApiFailures={handleMapApiFailure}
            onAuthError={refreshMapSas}
          />
        )}

        <AirportMarkers
          key={airportMapDetail.id}
          code={airportMapDetail.iataCode}
          name={airportMapDetail.name}
          position={airportMapDetail.location}
        />

        <Pane name="flight-tooltip-pane" style={{ zIndex: 800 }} />

        {!isInvalidFlight && (
          <Pane name="abc">
            <SelectedFlightMarker
              selectedFlightMarkerDetails={selectedFlightMarkerDetails}
              airportCode={airportMapDetail.iataCode}
              pane="abc"
              handleMarkerClick={handleMarkerClick}
            />
          </Pane>
        )}

        <DisplayMapErrors messages={mapApiFailures} />
      </MapContainer>
    </section>
  );
};

export default SingleFlightMap;
